import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type PackRow = {
  pack_code: string;
  label: string;
  credits: number;
  price_cents: number;
  currency: string;
  stripe_price_id: string | null;
  active: boolean;
  sort_order: number;
};

type PackTableRow = PackRow & { id: string };

export function MasterSmsPacksPage() {
  const [packs, setPacks] = useState<PackTableRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const [draftLabel, setDraftLabel] = useState('');
  const [draftCredits, setDraftCredits] = useState(100);
  const [draftPriceEuros, setDraftPriceEuros] = useState(5);
  const [draftStripe, setDraftStripe] = useState('');
  const [draftSort, setDraftSort] = useState(0);
  const [draftActive, setDraftActive] = useState(true);

  const [createCode, setCreateCode] = useState('');
  const [createLabel, setCreateLabel] = useState('');
  const [createCredits, setCreateCredits] = useState(100);
  const [createPriceEuros, setCreatePriceEuros] = useState(5);
  const [createStripe, setCreateStripe] = useState('');

  async function load() {
    setError(null);
    const res = await apiFetch('/api/v1/master/sms-packs');
    const body = (await res.json().catch(() => ({}))) as {
      packs?: PackRow[];
      error?: string;
      message?: string;
    };
    if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
    const list = Array.isArray(body.packs) ? body.packs : [];
    setPacks(list.map((p) => ({ ...p, id: p.pack_code })));
  }

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  function selectPack(pack: PackRow) {
    setSelectedCode(pack.pack_code);
    setDraftLabel(pack.label);
    setDraftCredits(Number(pack.credits) || 0);
    setDraftPriceEuros(Number(pack.price_cents || 0) / 100);
    setDraftStripe(String(pack.stripe_price_id || ''));
    setDraftSort(Number(pack.sort_order) || 0);
    setDraftActive(Boolean(pack.active));
    setMessage(null);
    setError(null);
  }

  async function savePack(payload: Record<string, unknown>, okMsg: string) {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/master/sms-packs', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ packs: [payload] }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      await load();
      setMessage(okMsg);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveSelected() {
    if (!selectedCode) return;
    const priceCents = Math.round(Number(draftPriceEuros) * 100);
    await savePack(
      {
        pack_code: selectedCode,
        label: draftLabel,
        credits: Number(draftCredits),
        price_cents: priceCents,
        stripe_price_id: draftStripe.trim() || null,
        sort_order: Number(draftSort) || 0,
        active: draftActive,
      },
      `Pack ${selectedCode} salvato`,
    );
  }

  async function createPack() {
    const code = createCode.trim().toLowerCase();
    const label = createLabel.trim() || code;
    const credits = Number(createCredits);
    const priceEuros = Number(createPriceEuros);
    const priceCents = Math.round(priceEuros * 100);
    const stripe = createStripe.trim();
    const sort = packs.length;
    await savePack(
      {
        pack_code: code,
        label,
        credits,
        price_cents: priceCents,
        stripe_price_id: stripe || null,
        active: true,
        sort_order: sort,
      },
      `Pack ${code} creato`,
    );
    setCreateCode('');
    setCreateLabel('');
    setCreateCredits(100);
    setCreatePriceEuros(5);
    setCreateStripe('');
    setSelectedCode(code);
    setDraftLabel(label);
    setDraftCredits(credits);
    setDraftPriceEuros(priceEuros);
    setDraftStripe(stripe);
    setDraftActive(true);
    setDraftSort(sort);
  }

  async function toggleActive(pack: PackRow) {
    await savePack(
      { pack_code: pack.pack_code, active: !pack.active },
      `Pack ${pack.pack_code} ${pack.active ? 'disattivato' : 'attivato'}`,
    );
    if (selectedCode === pack.pack_code) setDraftActive(!pack.active);
  }

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Pacchetti SMS"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · catalogo `sms_pack_catalog`
          </>
        }
      />
      <p className="sk-mc-hint" style={{ marginTop: '0.65rem' }}>
        Prezzo in euro → salvato come `price_cents`. Se `stripe_price_id` è vuoto, Checkout usa
        `price_data` dinamico.
      </p>

      {message ? <p className="sk-ok">{message}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      <SectionCard title={`${packs.length} pack`}>
        <DataTable
          emptyMessage="Nessun pack in catalogo"
          rows={packs}
          columns={[
            { key: 'pack_code', label: 'Codice' },
            { key: 'label', label: 'Label' },
            { key: 'credits', label: 'Crediti' },
            {
              key: 'price_cents',
              label: 'Prezzo',
              render: (v) => `€${(Number(v) / 100).toFixed(2)}`,
            },
            {
              key: 'active',
              label: 'Attivo',
              render: (v, row) => (
                <button
                  type="button"
                  className="sk-btn sk-btn--quiet"
                  disabled={busy}
                  onClick={() => void toggleActive(row)}
                >
                  {v ? 'Sì' : 'No'}
                </button>
              ),
            },
            {
              key: 'stripe_price_id',
              label: 'Stripe price',
              render: (v) => String(v || 'price_data'),
            },
            {
              key: 'actions',
              label: '',
              render: (_v, row) => (
                <button
                  type="button"
                  className="sk-btn sk-btn--quiet"
                  disabled={busy}
                  onClick={() => selectPack(row)}
                >
                  Modifica
                </button>
              ),
            },
          ]}
        />
      </SectionCard>

      <SectionCard title="Nuovo pack">
        <div className="sk-saas-form" style={{ marginTop: '0.85rem', maxWidth: '28rem' }}>
          <label className="sk-field">
            <span>pack_code</span>
            <input
              className="sk-input"
              value={createCode}
              onChange={(e) => setCreateCode(e.target.value)}
              placeholder="sms_250"
            />
          </label>
          <label className="sk-field">
            <span>Label</span>
            <input
              className="sk-input"
              value={createLabel}
              onChange={(e) => setCreateLabel(e.target.value)}
              placeholder="250 SMS"
            />
          </label>
          <label className="sk-field">
            <span>Crediti</span>
            <input
              className="sk-input"
              type="number"
              min={1}
              value={createCredits}
              onChange={(e) => setCreateCredits(Number(e.target.value))}
            />
          </label>
          <label className="sk-field">
            <span>€ prezzo</span>
            <input
              className="sk-input"
              type="number"
              min={0.01}
              step={0.01}
              value={createPriceEuros}
              onChange={(e) => setCreatePriceEuros(Number(e.target.value))}
            />
          </label>
          <label className="sk-field">
            <span>stripe_price_id (opz.)</span>
            <input
              className="sk-input"
              value={createStripe}
              onChange={(e) => setCreateStripe(e.target.value)}
              placeholder="price_… oppure vuoto → price_data"
            />
          </label>
          <button
            type="button"
            className="sk-btn"
            disabled={busy || !createCode.trim() || createCredits <= 0 || createPriceEuros <= 0}
            onClick={() => void createPack()}
          >
            Crea pack
          </button>
        </div>
      </SectionCard>

      {selectedCode ? (
        <SectionCard title={`Modifica · ${selectedCode}`}>
          <div className="sk-saas-form" style={{ marginTop: '0.85rem', maxWidth: '32rem' }}>
            <label className="sk-field">
              <span>Label</span>
              <input
                className="sk-input"
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
              />
            </label>
            <label className="sk-field">
              <span>Crediti</span>
              <input
                className="sk-input"
                type="number"
                min={1}
                value={draftCredits}
                onChange={(e) => setDraftCredits(Number(e.target.value))}
              />
            </label>
            <label className="sk-field">
              <span>€ prezzo</span>
              <input
                className="sk-input"
                type="number"
                min={0.01}
                step={0.01}
                value={draftPriceEuros}
                onChange={(e) => setDraftPriceEuros(Number(e.target.value))}
              />
            </label>
            <label className="sk-field">
              <span>stripe_price_id</span>
              <input
                className="sk-input"
                value={draftStripe}
                onChange={(e) => setDraftStripe(e.target.value)}
                placeholder="vuoto = price_data al checkout"
              />
            </label>
            <label className="sk-field">
              <span>sort_order</span>
              <input
                className="sk-input"
                type="number"
                value={draftSort}
                onChange={(e) => setDraftSort(Number(e.target.value))}
              />
            </label>
            <label
              className="sk-field"
              style={{ flexDirection: 'row', gap: '0.45rem', alignItems: 'center' }}
            >
              <input
                type="checkbox"
                checked={draftActive}
                onChange={(e) => setDraftActive(e.target.checked)}
              />
              <span>Attivo (visibile ai tenant)</span>
            </label>
            <button
              type="button"
              className="sk-btn"
              disabled={busy || !draftLabel.trim() || draftCredits <= 0 || draftPriceEuros <= 0}
              onClick={() => void saveSelected()}
            >
              Salva pack
            </button>
          </div>
      </SectionCard>
      ) : null}
    </section>
  );
}
